import type { FC, PropsWithChildren } from 'react';
import { createContext, useEffect, useMemo, useState } from 'react';

import { isValid, parseISO } from 'date-fns';
import omitBy from 'lodash-es/omitBy';
import throttle from 'lodash-es/throttle';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import type { EnvType } from 'api/types';
import { useQueryParams } from 'shared/lib/queryParams/useQueryParams';
import { StorageKeys, sessionStorageService } from 'shared/lib/storage/storageService';

import type { Filters } from '../lib/filters/common';
import { defaultFilters } from '../lib/filters/common';
import { isExpiryBucket } from '../lib/filters/expiryBuckets';
import { filtersToUrlParams } from '../lib/filters/filtersToUrlParams';
import { mergeFilters } from '../lib/filters/mergeFilters';
import { getPeriodTypeFromCode, isPresetPeriod, PeriodType, resolvePeriodFromParams } from '../lib/filters/period';
import { encodeQueryParams } from '../lib/queryParams/encodeQueryParams';
import { useSandboxContext } from '../lib/sandbox/useSandboxContext';
import { urls } from '../urls';

type FiltersContextType = {
  filters: Filters;
  filtersQuery: string;
  updateFilters: (filters: Partial<Filters>) => void;
  resetFilters: () => void;
};

const DATE_URL_KEYS = ['period', 'date_from', 'date_to'];

let storageFilters: Filters = {
  ...defaultFilters,
};

const possibleEnvs: EnvType[] = ['prod', 'dev', 'sandbox'];

const cachedFilters: Filters | undefined = sessionStorageService.get(StorageKeys.COMMON_FILTERS);

if (cachedFilters) {
  if (
    cachedFilters.env &&
    Array.isArray(cachedFilters.env) &&
    cachedFilters.env.every((item) => possibleEnvs.includes(item))
  ) {
    storageFilters.env = cachedFilters.env;
  }

  const cachedPeriodType = getPeriodTypeFromCode(cachedFilters.period ?? '');
  if (cachedPeriodType && isPresetPeriod(cachedPeriodType)) {
    storageFilters.period = cachedFilters.period;
    const resolved = resolvePeriodFromParams({ period: cachedFilters.period });
    storageFilters.date_from = resolved.date_from;
    storageFilters.date_to = resolved.date_to;
  } else if (
    cachedFilters.date_from &&
    isValid(parseISO(cachedFilters.date_from)) &&
    cachedFilters.date_to &&
    isValid(parseISO(cachedFilters.date_to))
  ) {
    storageFilters.period = PeriodType.Custom;
    storageFilters.date_from = cachedFilters.date_from;
    storageFilters.date_to = cachedFilters.date_to;
  }
}

const defaultContextValue: FiltersContextType = {
  filters: storageFilters,
  filtersQuery: encodeQueryParams(storageFilters),
  updateFilters: () => {},
  resetFilters: () => {},
};

export const FiltersContext = createContext(defaultContextValue);

export const FiltersContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { isSandboxOn } = useSandboxContext();
  const history = useHistory();

  const queryParams = useQueryParams<{
    env?: string | string[];
    period?: string;
    date_from?: string;
    date_to?: string;
    query?: string;
    page?: string;
    page_size?: string;
    expiry_bucket?: string;
  }>();

  const {
    env: queryParamsEnv,
    period: urlPeriod,
    date_from,
    date_to,
    query,
    page,
    page_size,
    expiry_bucket: urlExpiryBucket,
  } = queryParams;

  const env = queryParamsEnv
    ? ((Array.isArray(queryParamsEnv) ? queryParamsEnv : [queryParamsEnv]) as EnvType[])
    : null;

  const resolvedDates =
    urlPeriod || (date_from && date_to) ? resolvePeriodFromParams({ period: urlPeriod, date_from, date_to }) : null;

  const [filters, setFilters] = useState<Filters>({
    env: env?.every((item) => possibleEnvs.includes(item)) ? env : storageFilters.env,
    period: resolvedDates?.period ?? storageFilters.period,
    date_from: resolvedDates?.date_from ?? storageFilters.date_from,
    date_to: resolvedDates?.date_to ?? storageFilters.date_to,
    query: query ?? '',
    page: page ? Number(page) : storageFilters.page,
    page_size: page_size ? Number(page_size) : storageFilters.page_size,
    expiry_bucket: isExpiryBucket(urlExpiryBucket) ? urlExpiryBucket : undefined,
  });

  const filtersQuery = useMemo(() => {
    const { period: _, ...apiFilters } = filters;
    return encodeQueryParams(apiFilters);
  }, [filters]);

  const location = useLocation();

  const [lastFilterPathname, setLastFilterPathname] = useState(location.pathname);

  const updateFilters = throttle((partialFilters: Partial<Filters>) => {
    const newFilters = mergeFilters(filters, partialFilters);
    const currentPathname = location.pathname;

    const { query: _, page, page_size, ...filtersToCache } = newFilters;

    sessionStorageService.set(StorageKeys.COMMON_FILTERS, filtersToCache);

    const shouldResetPagination =
      currentPathname !== lastFilterPathname && !('page' in partialFilters) && !('page_size' in partialFilters);

    const filtersForUrl = shouldResetPagination
      ? {
          ...newFilters,
          page: defaultFilters.page,
          page_size: defaultFilters.page_size,
        }
      : newFilters;

    setFilters(filtersForUrl);

    const otherQueryParams = omitBy(
      queryParams,
      (_, key) => Object.keys(filtersForUrl).includes(key) || DATE_URL_KEYS.includes(key),
    );

    const urlParams = filtersToUrlParams(filtersForUrl);
    const search = encodeQueryParams({ ...urlParams, ...otherQueryParams });

    history.replace({ search });

    setLastFilterPathname(currentPathname);
  }, 500);

  const resetFilters = () => {
    storageFilters = { ...defaultFilters };

    const otherQueryParams = omitBy(
      queryParams,
      (_, key) => Object.keys(storageFilters).includes(key) || DATE_URL_KEYS.includes(key),
    );

    history.replace({ search: encodeQueryParams(otherQueryParams) });
    sessionStorageService.remove(StorageKeys.COMMON_FILTERS);
  };

  const matchNoFiltersPage = useRouteMatch({
    path: [
      urls.home.url(),
      urls.verification.url(),
      urls.logs.url(),
      urls.templates.url(),
      urls.emulator.url(),
      urls.billing.url(),
    ],
  });

  useEffect(() => {
    if (isSandboxOn != null) {
      const env: EnvType[] = isSandboxOn ? ['sandbox'] : ['dev', 'prod'];

      if (matchNoFiltersPage) {
        return;
      }

      if (env.some((item) => !filters.env.includes(item))) {
        updateFilters({ env });
      }
    }
  }, [isSandboxOn, updateFilters, matchNoFiltersPage, filters.env]);

  return (
    <FiltersContext.Provider value={{ filters, filtersQuery, updateFilters, resetFilters }}>
      {children}
    </FiltersContext.Provider>
  );
};
